import { useState } from 'react';

/**
 * WebP dönüşümü için özel React hook
 * Tarayıcıda Canvas API kullanarak JPEG/PNG resimlerini WebP formatına dönüştürür
 * 
 * @returns {Object} { convertToWebP, converting, error }
 */
export const useWebPConverter = () => {
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Dosyayı WebP formatına dönüştürür
   * @param {File} file - Dönüştürülecek dosya (JPEG, PNG, GIF)
   * @param {number} quality - WebP kalitesi (0.0 - 1.0), varsayılan: 1.0 (100%)
   * @returns {Promise<File>} WebP formatında yeni File objesi
   */
  const convertToWebP = async (file, quality = 1.0) => {
    // Sadece resim dosyalarını dönüştür
    if (!file.type.startsWith('image/')) {
      throw new Error('Sadece resim dosyaları dönüştürülebilir');
    }

    // Zaten WebP ise dönüştürmeye gerek yok
    if (file.type === 'image/webp') {
      return file;
    }

    setConverting(true);
    setError(null);

    try {
      // Canvas API desteğini kontrol et
      if (!HTMLCanvasElement.prototype.toBlob) {
        throw new Error('Tarayıcınız WebP dönüşümünü desteklemiyor (Canvas toBlob API eksik)');
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          const img = new Image();

          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;

              const ctx = canvas.getContext('2d');
              
              // PNG/GIF için şeffaflığı korumak için arka plan ekleme
              // Canvas'a direkt çiz, şeffaflık korunur
              
              // Resmi canvas'a çiz
              ctx.drawImage(img, 0, 0);

              // Canvas'ı WebP formatında blob'a dönüştür
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    setConverting(false);
                    reject(new Error('WebP dönüşümü başarısız oldu - tarayıcı WebP formatını desteklemiyor olabilir'));
                    return;
                  }

                  // Blob'u File objesine dönüştür
                  const webpFileName = file.name.replace(/\.(jpg|jpeg|png|gif)$/i, '.webp');
                  const webpFile = new File([blob], webpFileName, {
                    type: 'image/webp',
                    lastModified: Date.now()
                  });

                  console.log(`✅ WebP dönüşümü başarılı: ${file.name} -> ${webpFileName} (${(blob.size / 1024).toFixed(2)} KB)`);
                  setConverting(false);
                  resolve(webpFile);
                },
                'image/webp',
                quality
              );
            } catch (err) {
              setConverting(false);
              setError(err.message);
              reject(err);
            }
          };

          img.onerror = () => {
            setConverting(false);
            const err = new Error('Resim yüklenemedi');
            setError(err.message);
            reject(err);
          };

          img.src = e.target.result;
        };

        reader.onerror = () => {
          setConverting(false);
          const err = new Error('Dosya okunamadı');
          setError(err.message);
          reject(err);
        };

        reader.readAsDataURL(file);
      });
    } catch (err) {
      setConverting(false);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Birden fazla dosyayı toplu olarak WebP'ye dönüştürür
   * @param {File[]} files - Dönüştürülecek dosya dizisi
   * @param {number} quality - WebP kalitesi (0.0 - 1.0)
   * @returns {Promise<File[]>} WebP formatında dosya dizisi
   */
  const convertMultipleToWebP = async (files, quality = 1.0) => {
    const fileArray = Array.isArray(files) ? files : Array.from(files);
    console.log(`🔄 ${fileArray.length} dosya WebP'ye dönüştürülüyor...`);
    
    const conversionPromises = fileArray.map(async (file) => {
      // Video dosyalarını ve zaten WebP olan dosyaları dönüştürme
      if (!file.type.startsWith('image/') || file.type === 'image/webp') {
        console.log(`⏭️  ${file.name} atlandı (video veya zaten WebP)`);
        return file;
      }
      
      try {
        return await convertToWebP(file, quality);
      } catch (err) {
        console.error(`❌ ${file.name} dönüştürülemedi:`, err.message);
        console.warn(`⚠️  ${file.name} için orijinal dosya kullanılacak`);
        return file; // Hata durumunda orijinal dosyayı döndür
      }
    });

    const results = await Promise.all(conversionPromises);
    const convertedCount = results.filter((f, index) => {
      const original = fileArray[index];
      return f.type === 'image/webp' && original.type !== 'image/webp';
    }).length;
    console.log(`✅ Dönüşüm tamamlandı: ${convertedCount}/${fileArray.length} dosya WebP'ye dönüştürüldü`);
    
    return results;
  };

  return { 
    convertToWebP, 
    convertMultipleToWebP,
    converting, 
    error 
  };
};
